import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
	DiemDialogComponent,
	TransactionDialogComponent,
	TripDialogComponent,
	TimeSheetHistoryDialogComponent
} from '../dialogs';
import { IDiem, ITransaction, ITrip, ITimeSheetData, ITimeSheet } from './time-sheets.type';
import * as moment from 'moment';
import { TimeSheetsStoreService } from './time-sheets.store';
import { flatMap, map, takeUntil, skip } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { TimeSheetStatusEnum } from './time-sheet-status.enum';
import { SessionService, TimeSheetVisibilityService } from '../../core';
import { TimeSheetStatusErrorEnum } from './time-sheet-status-error.enum';
import { TimeSheetStatusButtonsEnum } from './time-sheet-status-buttons.enum';
import * as _ from 'lodash';
import { MatDrawer } from '@angular/material/sidenav';
import { Subject } from 'rxjs';
import { PilotPayAuthResponseOptions } from '../../types/auth.type';

/**
 * AppComponent
 */
@Component({
	selector: 'app-pilotpay-time-sheets',
	styleUrls: ['./time-sheets.component.scss'],
	templateUrl: './time-sheets.component.html'
})

/**
 * TimeSheetsComponent
 */
export class TimeSheetsComponent implements OnDestroy, OnInit {
	get timeSheetStatusEnum(): any {
		return TimeSheetStatusEnum;
	}

	get timeSheetStatusButtonsEnum(): any {
		return TimeSheetStatusButtonsEnum;
	}
	constructor(
		public dialog: MatDialog,
		private activatedRoute: ActivatedRoute,
		private breakpointObserver: BreakpointObserver,
		private router: Router,
		private timeSheetsStoreService: TimeSheetsStoreService,
		private timeSheetVisibilityService: TimeSheetVisibilityService,
		private sessionService: SessionService
	) {}

	public loggedInUserDetails: PilotPayAuthResponseOptions = this.sessionService.getUser();

	public disabledSubmitBtn: boolean;
	public displayedTripColumns: string[] = [
		'tripStart',
		'routeInformation',
		'tripNumber',
		'tripStatus',
		'loadsUnloadsCount',
		'edit',
		'history'
	];
	public displayedTransactionColumns: string[] = ['date', 'value', 'category', 'icon', 'notes', 'edit', 'history'];
	public errorMessage: string;
	public diemDialogRef: MatDialogRef<DiemDialogComponent>;
	public isMobile: boolean = false;
	public isTimeSheetLocked: boolean = false;
	public transactionDialogRef: MatDialogRef<TransactionDialogComponent>;
	public timeSheetHistoryDialogRef: MatDialogRef<TimeSheetHistoryDialogComponent>;
	public tripDialogRef: MatDialogRef<TripDialogComponent>;
	public selectedDate: string;
	public timeSheetData: ITimeSheetData;
	public timeSheets: ITimeSheet[] = [];
	public showTotal: boolean = false;
	public perDiemsWithErrors: IDiem[];
	public tripsWithErrors: ITrip[];
	public tripsWithNoPerDiems: ITrip[];
	public currentDate: string;
	public previousDate: string;

	public timeSheetLockOutError: string = 'No further edits can be made to this time sheet.';
	public hasError: boolean;

	@ViewChild('drawer', { static: false }) public drawer: MatDrawer;

	private ngUnsubscribe: Subject<void> = new Subject<void>();
	private timeSheetStatusError: string;

	public ngOnDestroy(): void {
		this.timeSheetVisibilityService.setTimeSheetMobileVisibility(false);
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	public ngOnInit(): void {
		// Inserts the variance approval column before the edit button
		if (this.sessionService.getUser().isAdmin) {
			this.displayedTripColumns.splice(this.displayedTripColumns.length - 2, 0, 'tripVariesFromCmsData');
		}

		// Watches for any changes to the employee param or date queryParam
		// Upon change to any of the 2 it should refresh the timeSheet
		this.activatedRoute.params
			.pipe(
				flatMap(param => {
					return this.activatedRoute.queryParams.pipe(
						map(queryParam => {
							return { ...queryParam, ...param };
						})
					);
				})
			)
			.subscribe(response => {
				this.timeSheetsStoreService.getCurrentTimeSheetData(response.employeeId, response.date);
			});

		this.timeSheetsStoreService.timeSheet$
			.pipe(
				skip(1),
				takeUntil(this.ngUnsubscribe)
			)
			.subscribe((response: ITimeSheetData) => {
				if (response) {
					this.timeSheetData = response;
					if (response && response.timeSheet) {
						this.extractTimeSheetErrors();
						this.selectedDate = this.timeSheetData.timeSheet.startDate.toString();

						// Is the current day after or on the lock out day (the 5th of the following month)
						this.isTimeSheetLocked =
							this.isAfterOrOnLockoutDate(
								moment.utc(),
								this.timeSheetData.timeSheet.startDate,
								this.timeSheetData.timeSheet.timeSheetLockOutDay
							) && !this.sessionService.getUser().isAdmin;

						this.processTimeSheetStatus(response);
					}

					// Display flightHours column only for non-loadspecialists
					if (
						response.currentEmployee &&
						!response.currentEmployee.isLoadSpecialist &&
						!this.displayedTripColumns.includes('flightHours')
					) {
						this.displayedTripColumns.splice(3, 0, 'flightHours');
					}
				}
			});

		this.timeSheetsStoreService.timeSheets$
			.pipe(
				skip(1),
				takeUntil(this.ngUnsubscribe)
			)
			.subscribe((response: ITimeSheet[]) => {
				this.currentDate = moment
					.utc()
					.set('date', 1)
					.set('month', moment.utc().get('month'))
					.startOf('day')
					.format('YYYY-MM-DDTHH:mm:ss');
				this.previousDate = moment
					.utc()
					.subtract(1, 'month')
					.set('date', 1)
					.startOf('day')
					.format('YYYY-MM-DDTHH:mm:ss');
				this.timeSheets = _.orderBy(response, o => moment.utc(o.startDate), ['desc']);
			});

		window.addEventListener('scroll', this.scroll, true);

		this.breakpointObserver
			.observe(['(max-width: 1230px)'])
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe((state: BreakpointState) => {
				this.isMobile = state.matches;
				this.showTotal = !state.matches;

				if (!state.matches) {
					this.timeSheetVisibilityService.setTimeSheetMobileVisibility(false);
				}
			});
	}

	public isAfterOrOnLockoutDate(currentDate: moment.Moment, timeSheetStartDate: string, lockOutDay: number): boolean {
		return currentDate.isSameOrAfter(
			moment
				.utc()
				.set('date', lockOutDay)
				.set('year', moment.utc(timeSheetStartDate).get('year'))
				.set('month', moment.utc(timeSheetStartDate).get('month'))
				.add(1, 'month')
				.startOf('day')
		);
	}

	public onTotalToggle(): void {
		this.showTotal = !this.showTotal;
		this.drawer.toggle().then();
		this.timeSheetVisibilityService.setTimeSheetMobileVisibility(this.showTotal && this.isMobile);
	}

	public monthSelectedHandler(date: any): void {
		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams: { date: date }
		});
	}

	/**
	 * Diem Dialog
	 */
	public openDiemDialog(perDiem?: IDiem): void {
		const dialogConfig: MatDialogConfig = new MatDialogConfig();

		dialogConfig.disableClose = true;
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			timesheet: this.timeSheetData.timeSheet,
			perDiem: perDiem,
			timeSheetStatusError: this.timeSheetStatusError,
			timeSheetLockOutError: this.isTimeSheetLocked ? this.timeSheetLockOutError : '',
			isMobile: this.isMobile
		};

		if (this.dialog) {
			this.diemDialogRef = this.dialog.open(DiemDialogComponent, dialogConfig);
		}
	}

	/**
	 * Trip Dialog
	 */
	public openTripDialog(trip?: ITrip): void {
		const dialogConfig: MatDialogConfig = new MatDialogConfig();

		dialogConfig.disableClose = true;
		dialogConfig.autoFocus = true;

		dialogConfig.data = {
			timeSheet: this.timeSheetData.timeSheet,
			trip: trip,
			currentEmployee: this.timeSheetData.currentEmployee,
			timeSheetStatusError: this.timeSheetStatusError,
			timeSheetLockOutError: this.isTimeSheetLocked ? this.timeSheetLockOutError : '',
			isMobile: this.isMobile
		};

		if (this.dialog) {
			this.tripDialogRef = this.dialog.open(TripDialogComponent, dialogConfig);
		}
	}

	/**
	 * Transaction Dialog
	 */
	public openTransactionDialog(transaction?: ITransaction): void {
		// Load Specialists should only see pay categories that have been marked "Visible to load specialists"
		if (this.timeSheetData.currentEmployee.isLoadSpecialist) {
			this.timeSheetData.payCategories = this.timeSheetData.payCategories.filter(
				item => item.isVisibleToLoadSpecialists
			);
		}
		const dialogConfig: MatDialogConfig = new MatDialogConfig();

		dialogConfig.disableClose = true;
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			timesheet: this.timeSheetData.timeSheet,
			transaction: transaction,
			payCategories: this.timeSheetData.payCategories,
			timeSheetStatusError: this.timeSheetStatusError,
			timeSheetLockOutError: this.isTimeSheetLocked ? this.timeSheetLockOutError : '',
			isMobile: this.isMobile
		};

		if (this.dialog) {
			this.transactionDialogRef = this.dialog.open(TransactionDialogComponent, dialogConfig);
		}
	}

	public calculateTimeDifference(perdiem: IDiem): string {
		return moment
			.utc(perdiem.perDiemEnd)
			.diff(moment.utc(perdiem.perDiemStart), 'hours', true)
			.toFixed(2);
	}

	public convertTimeToHours(flightMinutes: number): string {
		const hours: number = Math.floor(flightMinutes / 60);
		const minutes: number = flightMinutes % 60;
		return `${hours.toString()}h ${minutes.toString()}m`;
	}

	public submitTimeSheet(): void {
		const isAdmin: boolean = this.sessionService.getUser().isAdmin;
		const status: number = this.timeSheetData.timeSheet.status;
		let statusValue: number = 0;

		if (isAdmin) {
			if (status === TimeSheetStatusEnum.Paid) {
				return;
			}
			statusValue =
				status === TimeSheetStatusEnum.ApprovedByAdmin
					? TimeSheetStatusEnum.InProgress
					: TimeSheetStatusEnum.ApprovedByAdmin;
		} else {
			if (status === TimeSheetStatusEnum.ApprovedByAdmin || status === TimeSheetStatusEnum.Paid) {
				return;
			}
			statusValue =
				status === TimeSheetStatusEnum.SubmittedByEmployee
					? TimeSheetStatusEnum.InProgress
					: TimeSheetStatusEnum.SubmittedByEmployee;
		}
		this.timeSheetsStoreService.submitTimeSheet(statusValue).subscribe();
	}

	public toggleTripVarianceApproval(event: boolean, trip: ITrip): void {
		this.timeSheetsStoreService.approveTrip(trip.timeSheetTripId, event).subscribe();
	}

	public openTimeSheetHistoryDialog(timeSheetData: ITransaction | ITrip): void {
		const dialogConfig: MatDialogConfig = new MatDialogConfig();

		dialogConfig.disableClose = true;
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			timeSheetData: timeSheetData,
			isMobile: this.isMobile
		};

		if (this.dialog) {
			this.timeSheetHistoryDialogRef = this.dialog.open(TimeSheetHistoryDialogComponent, dialogConfig);
		}
	}

	// An Admin can edit a time-sheet only when its status is not Paid
	// A Pilot can edit a time-sheet only when its status is NoStarted or InProgress
	private processTimeSheetStatus(response: ITimeSheetData): void {
		const isAdmin: boolean = this.sessionService.getUser().isAdmin;
		const status: number = response.timeSheet.status;
		const isAnyVarianceNotApproved: boolean =
			this.timeSheetData.trips.findIndex(x => x.tripVariesFromCmsData && !x.varianceIsApproved) !== -1;
		this.disabledSubmitBtn =
			(!isAdmin && (status === TimeSheetStatusEnum.ApprovedByAdmin || status === TimeSheetStatusEnum.Paid)) ||
			this.isTimeSheetLocked ||
			(isAdmin && status === TimeSheetStatusEnum.Paid) ||
			(isAdmin && isAnyVarianceNotApproved && status !== TimeSheetStatusEnum.ApprovedByAdmin);

		if (
			(isAdmin && status !== TimeSheetStatusEnum.Paid) ||
			(!isAdmin && status === TimeSheetStatusEnum.NotStarted) ||
			(!isAdmin && status === TimeSheetStatusEnum.InProgress)
		) {
			this.timeSheetStatusError = null;
			return;
		}
		this.timeSheetStatusError = TimeSheetStatusErrorEnum[TimeSheetStatusEnum[status]];
	}

	private scroll(event: any): void {
		const element: any = document.getElementById('sticky');
		if (element && event.target.className !== 'mat-drawer-inner-container') {
			if (event.srcElement.scrollTop) {
				element.style.top = '0px';
			} else {
				element.style.top = '128px';
			}
		}
	}

	private extractTimeSheetErrors(): void {
		this.perDiemsWithErrors = this.timeSheetData.perDiems.filter(perdiem => perdiem.errors.length);
		this.tripsWithErrors = this.timeSheetData.trips.filter(trip => {
			trip.commentNeededError = trip.cmsOriginalLogPageNumber && trip.tripVariesFromCmsData && !trip.comments;
			return trip.commentNeededError;
		});
		this.tripsWithNoPerDiems = this.timeSheetData.tripsAndPerDiems
			.filter(item => !item.perDiem && item.trips.length)
			.map(e => e.trips.map(link => link))
			.reduce((a, b) => a.concat(b), []);

		this.timeSheetData.timeSheet.status === this.timeSheetStatusEnum.Paid ||
		(!this.sessionService.getUser().isAdmin &&
			this.timeSheetData.timeSheet.status === this.timeSheetStatusEnum.ApprovedByAdmin) ||
		(!this.sessionService.getUser().isAdmin &&
			this.timeSheetData.timeSheet.status === this.timeSheetStatusEnum.SubmittedByEmployee)
			? (this.hasError = true)
			: (this.hasError = false);
	}
}
